import { useCallback, useMemo } from "react";
import {
  TICKET_TEMPLATES_QUERY,
  useTicketTemplateCreateMutation,
} from "@ticketingplatform/api/dist/ticketTemplate";
import { Form, message } from "antd";
import useApolloErrorFields from "hooks/useApolloErrorFields";
import { useIntl } from "react-intl";
import { useVenueContext } from "contexts/VenueContext";
import { useNavigate } from "react-router-dom";
import { ROUTE_PATHS } from "const/paths";
import usePrompt from "hooks/usePrompt";

import { TicketTemplateFormValues } from "./partials/TicketTemplateForm/types";
import {
  UseTicketTemplateCreate,
  UseTicketTemplateCreateReturnType,
} from "./types";
import { initialValues } from "./consts";
import { pickTicketPoolFormFields } from "./utils";

export const useTicketTemplateCreate: UseTicketTemplateCreate = () => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const { venue } = useVenueContext();
  const navigate = useNavigate();

  const [ticketTemplateCreateMutation, { loading, error }] =
    useTicketTemplateCreateMutation({
      evictQueries: [TICKET_TEMPLATES_QUERY],
    });

  const fields = useApolloErrorFields(error);

  usePrompt(loading);

  const onFinish = useCallback(
    async ({ input: { name, ticketPools } }: TicketTemplateFormValues) => {
      try {
        await ticketTemplateCreateMutation({
          variables: {
            input: {
              name,
              venueId: venue?.id,
              ticketPools: ticketPools?.map(({ id: _, ...ticketPool }) =>
                pickTicketPoolFormFields(ticketPool)
              ),
            },
          },
        });

        message.success(
          intl.formatMessage({
            defaultMessage: "Ticket Template has been added!",
          })
        );

        navigate(ROUTE_PATHS.TicketTemplateList, {
          state: {
            skipBlock: true,
          },
        });
      } catch {
        message.error(
          intl.formatMessage({
            defaultMessage: "Error. Cannot add Ticket Template.",
          })
        );
      }
    },
    [intl, navigate, ticketTemplateCreateMutation, venue?.id]
  );

  return useMemo<UseTicketTemplateCreateReturnType>(
    () => ({
      form: {
        initialValues,
        fields,
        loading,
        form,
        onFinish,
      },
    }),
    [fields, form, loading, onFinish]
  );
};
